import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';

export interface ProductIdCellProps {
  productId: string;
}

/**
 * Displays a raw product id — merchant-portal cannot resolve product NAMES here (catalog has no
 * bulk-by-ids lookup readily reusable from web-inventory-service without a backend change, out of
 * scope for this frontend-only feature; see this feature's `index.ts` for the follow-up note).
 * Shown truncated + monospace (JetBrains Mono, the design system's data-only mono convention),
 * with the full id in a tooltip and a copy-to-clipboard action — the honest, usable compromise.
 */
export function ProductIdCell({ productId }: ProductIdCellProps) {
  const { t } = useTranslation('inventory');
  const [copied, setCopied] = useState(false);
  const truncated = productId.length > 10 ? `${productId.slice(0, 8)}…` : productId;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(productId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can be unavailable (older browsers, insecure context) — the full id is
      // still visible in the tooltip below for manual selection/copy, so this is a silent no-op.
    }
  };

  return (
    <Stack direction="row" spacing={0.25} alignItems="center">
      <Tooltip title={productId}>
        <Typography
          variant="caption"
          sx={{ fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'nowrap' }}
        >
          {truncated}
        </Typography>
      </Tooltip>
      <Tooltip title={copied ? t('productIdCell.copied') : t('productIdCell.copyAria')}>
        <IconButton size="small" aria-label={t('productIdCell.copyAria')} onClick={handleCopy}>
          <ContentCopyRoundedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
